import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useGroup, useUpdateGroup, useDeleteGroups } from '../../../hooks/admin/useGroups';
import { useAssignGroup } from '../../../hooks/admin/useDevices';
import ConfirmDeleteDialog from '../../../components/admin/ConfirmDeleteDialog';
import { GroupEditTabs, type GroupEditTab } from '../../../components/admin/groups/GroupEditTabs';
import { UNASSIGNED_LOCATION_ID } from '../../../constants/groups';
import type { GroupUpdateRequest } from '../../../types/admin';

// The system group cannot be deleted, so it has no Manage tab.
const tabsFor = (isSystemGroup: boolean): GroupEditTab[] =>
  isSystemGroup ? ['details', 'users', 'rics'] : ['details', 'users', 'rics', 'manage'];

export default function GroupEdit() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const locationId = parseInt(id || '0');
  const isSystemGroup = locationId === UNASSIGNED_LOCATION_ID;

  // Tab state lives in the URL, as on Edit User, so a tab can be linked to and
  // survives a refresh. An unknown tab (or Manage on the system group) falls
  // back to Details rather than rendering nothing.
  const tabs = tabsFor(isSystemGroup);
  const activeTab: GroupEditTab = (tabs as string[]).includes(tab ?? '') ? (tab as GroupEditTab) : 'details';

  const { data, isLoading } = useGroup(locationId);
  const updateGroup = useUpdateGroup();
  const deleteGroups = useDeleteGroups();
  const assignGroup = useAssignGroup();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<number>>(new Set());

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<GroupUpdateRequest>({
    values: data?.group ? {
      locationName: data.group.locationName,
      latitude: data.group.latitude,
      longitude: data.group.longitude,
      description: data.group.description || '',
    } : undefined,
  });

  const allDeviceIds = useMemo(
    () => (data?.devices ?? []).map((d) => d.deviceId),
    [data?.devices],
  );
  const allSelected = allDeviceIds.length > 0 && selectedDeviceIds.size === allDeviceIds.length;
  const someSelected = selectedDeviceIds.size > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedDeviceIds(allSelected ? new Set() : new Set(allDeviceIds));
  };

  const toggleOne = (deviceId: number) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const clearSelection = () => setSelectedDeviceIds(new Set());

  const handleUnassignSelected = async () => {
    if (selectedDeviceIds.size === 0) return;
    try {
      await assignGroup.mutateAsync({
        deviceIds: Array.from(selectedDeviceIds),
        locationId: UNASSIGNED_LOCATION_ID,
      });
      clearSelection();
    } catch (error) {
      console.error('Failed to unassign devices:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGroups.mutateAsync([locationId]);
      setShowDeleteDialog(false);
      navigate('/admin/groups');
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const onSubmit = async (formData: GroupUpdateRequest) => {
    try {
      await updateGroup.mutateAsync({ id: locationId, data: formData });
      reset(formData);
    } catch (error) {
      console.error('Failed to update group:', error);
    }
  };

  // Changing tab unmounts the form, so unsaved edits would go with it. Ask
  // before that happens rather than losing them quietly.
  const handleTabChange = (next: GroupEditTab) => {
    if (next === activeTab) return;
    if (activeTab === 'details' && isDirty) {
      if (!window.confirm('You have unsaved changes on this tab. Leave without saving?')) return;
      reset();
    }
    navigate(`/admin/groups/${locationId}/edit/${next}`);
  };

  if (isLoading) return <div>Loading group...</div>;
  if (!data) return <div>Group not found</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold">{isSystemGroup ? 'Unassigned' : 'Edit Group'}</h1>
        {isSystemGroup && (
          <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded">
            System group
          </span>
        )}
      </div>

      <GroupEditTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
        counts={{ users: data.users.length, rics: data.devices.length }}
        dirtyTabs={isDirty ? ['details'] : []}
      />

      <div className="mt-6">
        {activeTab === 'details' && (
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6">
            {isSystemGroup && (
              <p className="mb-4 text-sm text-gray-600">
                This is the system-managed group for RICs that have not been assigned to a real location.
                Its name and coordinates cannot be changed.
              </p>
            )}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Group Name *</label>
                <input
                  type="text"
                  {...register('locationName', { required: 'Group name is required' })}
                  disabled={isSystemGroup}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                />
                {errors.locationName && (
                  <p className="text-red-600 text-sm mt-1">{errors.locationName.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Latitude *</label>
                  <input
                    type="number"
                    step="any"
                    {...register('latitude', {
                      required: 'Latitude is required',
                      min: { value: -90, message: 'Must be between -90 and 90' },
                      max: { value: 90, message: 'Must be between -90 and 90' },
                      valueAsNumber: true,
                    })}
                    disabled={isSystemGroup}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  {errors.latitude && (
                    <p className="text-red-600 text-sm mt-1">{errors.latitude.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Longitude *</label>
                  <input
                    type="number"
                    step="any"
                    {...register('longitude', {
                      required: 'Longitude is required',
                      min: { value: -180, message: 'Must be between -180 and 180' },
                      max: { value: 180, message: 'Must be between -180 and 180' },
                      valueAsNumber: true,
                    })}
                    disabled={isSystemGroup}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  {errors.longitude && (
                    <p className="text-red-600 text-sm mt-1">{errors.longitude.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea
                  {...register('description')}
                  rows={4}
                  disabled={isSystemGroup}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              {!isSystemGroup && (
                <div className="flex gap-4 justify-end">
                  <button
                    type="submit"
                    disabled={!isDirty || updateGroup.isPending}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updateGroup.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/admin/groups')}
                    className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </form>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Users who have access to this Group</h2>
            {data.users.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">User ID</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Email</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Name</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Logins</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((user) => (
                      <tr key={user.userId} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-2">{user.userId}</td>
                        <td className="px-4 py-2">{user.email}</td>
                        <td className="px-4 py-2">{user.firstName} {user.lastName}</td>
                        <td className="px-4 py-2">{user.logins}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-600">No users have access to this group.</p>
            )}
          </div>
        )}

        {activeTab === 'rics' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">RICs that are assigned to this Group</h2>

            {!isSystemGroup && selectedDeviceIds.size > 0 && (
              <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded">
                <span className="font-medium text-blue-900">
                  {selectedDeviceIds.size} selected
                </span>
                <span className="text-blue-700">·</span>
                <button
                  type="button"
                  onClick={handleUnassignSelected}
                  disabled={assignGroup.isPending}
                  className="px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {assignGroup.isPending ? 'Unassigning...' : 'Unassign from group'}
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="px-4 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  Clear selection
                </button>
              </div>
            )}

            {data.devices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {!isSystemGroup && (
                        <th
                          className="px-4 py-2 text-left text-sm font-medium text-gray-600 w-12 cursor-pointer"
                          onClick={toggleAll}
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 cursor-pointer"
                            checked={allSelected}
                            ref={(el) => { if (el) el.indeterminate = someSelected; }}
                            onChange={toggleAll}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Select all RICs"
                          />
                        </th>
                      )}
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Serial #</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Hardware</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Sigfox ID</th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.devices.map((device) => {
                      const isChecked = selectedDeviceIds.has(device.deviceId);
                      return (
                        <tr
                          key={device.deviceId}
                          onClick={() => navigate(`/admin/devices/${device.deviceId}/edit`)}
                          className={`border-t cursor-pointer transition-colors ${
                            isChecked ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                          }`}
                        >
                          {!isSystemGroup && (
                            <td
                              className="px-4 py-3 w-12"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOne(device.deviceId);
                              }}
                            >
                              <input
                                type="checkbox"
                                className="w-4 h-4 cursor-pointer"
                                checked={isChecked}
                                onChange={() => toggleOne(device.deviceId)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select RIC ${device.serialNumber}`}
                              />
                            </td>
                          )}
                          <td className="px-4 py-2 font-medium">{device.serialNumber}</td>
                          <td className="px-4 py-2">{device.hardwareVersion}</td>
                          <td className="px-4 py-2">{device.sigfoxId}</td>
                          <td className="px-4 py-2">{device.sigfoxRegistered ? 'Y' : 'N'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-600">No devices are assigned to this group.</p>
            )}
          </div>
        )}

        {/* Manage: what is done TO the group rather than edited on it, kept off
            the Details tab so Delete is never one stray click below Save. Not
            offered for the system group. */}
        {activeTab === 'manage' && !isSystemGroup && (
          <div className="bg-white border border-red-300 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-red-700 mb-2">Danger Zone</h2>
            <p className="text-gray-600 mb-4 max-w-2xl">
              Deleting this group will unassign all of its devices and remove all user access. This action cannot be undone.
            </p>
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete Group
            </button>
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Group"
        message={`This will permanently delete "${data.group.locationName}", unassign its devices, and remove all user access. This cannot be undone.`}
        confirmText={data.group.locationName}
        isLoading={deleteGroups.isPending}
      />
    </div>
  );
}
